import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MOMENT_DATE_FORMAT_DASH_REVERSED } from '@app/app.constants';
import { environment } from '@root/environments/environment';
import moment from 'moment';
import { map, Observable } from 'rxjs';
import { PricingPartnerFilters } from '../store/pricing-partner.state';
import {
  BundlePricePivot,
  BundlePricePivotApiModelData,
  Discount,
  DiscountApiModelData,
  PartnerBundle,
  PricingPartner,
  PricingPartnerApiModel,
  PricingPartnerApiModelData,
  PricingPartnerList,
} from '../types/pricing.types';
import { DiscountUtilsService } from './discount-utils.service';
import { PricingPartnerUtilsService } from './pricing-partner-utils.service';

@Injectable({
  providedIn: 'root',
})
export class PricingPartnerService {
  private http = inject(HttpClient);
  private pricingPartnerUtilsService = inject(PricingPartnerUtilsService);
  private discountUtilsService = inject(DiscountUtilsService);

  private readonly API_URL = `${environment.apiUrl}/nipt-invoicing`;

  public getPricingPartner(id: string): Observable<PricingPartner> {
    return this.http
      .get<PricingPartnerApiModelData>(`${this.API_URL}/partners/${id}`)
      .pipe(
        map((model: PricingPartnerApiModelData) =>
          this.pricingPartnerUtilsService.apiModelToPricingPartner(model)
        )
      );
  }

  public getPricingPartnersList(
    filters: PricingPartnerFilters
  ): Observable<PricingPartnerList> {
    return this.http
      .get<PricingPartnerApiModel>(`${this.API_URL}/partners`, {
        params:
          this.pricingPartnerUtilsService.buildPricingPartnerHttpParams(
            filters
          ),
      })
      .pipe(
        map((model: PricingPartnerApiModel) => {
          return {
            data: model.data.map((model: PricingPartnerApiModelData) =>
              this.pricingPartnerUtilsService.apiModelToPricingPartner(model)
            ),
            totalCount: model.total_count,
            hasMorePages: model.has_more_pages,
            perPage: model.per_page,
            currentPage: model.current_page,
          };
        })
      );
  }

  public updatePricingPartner(
    partnerId: string,
    body: { currency?: string; notes?: string }
  ): Observable<void> {
    return this.http.put<void>(`${this.API_URL}/partners/${partnerId}`, body);
  }

  public getPricingPartnerBundles(
    partnerId: string
  ): Observable<PartnerBundle[]> {
    return this.http.get<PartnerBundle[]>(
      `${this.API_URL}/partners/${partnerId}/bundles`
    );
  }

  public addBundleToPricingPartner(
    partnerId: string,
    bundleId: string,
    price: number,
    validFrom: Date
  ): Observable<{ price: number }> {
    return this.http.post<{ price: number }>(
      `${this.API_URL}/partners/${partnerId}/bundles/${bundleId}`,
      {
        price,
        valid_from: validFrom
          ? moment(validFrom).format(MOMENT_DATE_FORMAT_DASH_REVERSED)
          : null,
      }
    );
  }

  public updatePricingPartnerBundlePrice(
    partnerId: string,
    pivotId: string,
    price: number
  ): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/partners/${partnerId}/bundles/${pivotId}`,
      { price }
    );
  }

  public deletePricingPartnerBundlePrice(
    partnerId: string,
    pivotId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.API_URL}/partners/${partnerId}/bundles/${pivotId}`
    );
  }

  public approvePricingPartnerPriceRequest(
    partnerId: string,
    requestId: string
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.API_URL}/partners/${partnerId}/price-requests/${requestId}/approve`,
      {}
    );
  }

  public deletePricingPartnerPriceRequest(
    partnerId: string,
    requestId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.API_URL}/partners/${partnerId}/price-requests/${requestId}`
    );
  }

  public getPricingPartnerDiscounts(partnerId: string): Observable<Discount[]> {
    return this.http
      .get<
        DiscountApiModelData[]
      >(`${this.API_URL}/partners/${partnerId}/discounts`)
      .pipe(
        map((discounts: DiscountApiModelData[]) =>
          discounts.map((discount: DiscountApiModelData) =>
            this.discountUtilsService.apiModelToDiscount(discount)
          )
        )
      );
  }

  public getPricingPartnerBundlePriceHistory(
    partnerId: string,
    bundleId: string
  ): Observable<BundlePricePivot[]> {
    return this.http
      .get<
        BundlePricePivotApiModelData[]
      >(`${this.API_URL}/partners/${partnerId}/bundles/${bundleId}/history`)
      .pipe(
        map((history: BundlePricePivotApiModelData[]) =>
          history.map((history: BundlePricePivotApiModelData) =>
            this.pricingPartnerUtilsService.apiModelToPricingPartnerBundlePivot(
              history
            )
          )
        )
      );
  }

  public exportPricelist(
    filters: PricingPartnerFilters
  ): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.API_URL}/export/pricelist`, {
        responseType: 'blob',
        observe: 'response',
        params:
          this.pricingPartnerUtilsService.buildPricingPartnerHttpParams(
            filters
          ),
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          blob: response.body,
          filename: response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            .split(';')[0],
        }))
      );
  }

  public exportBilling(params: {
    from: string;
    to: string;
  }): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.API_URL}/export/billing`, {
        responseType: 'blob',
        observe: 'response',
        params,
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          blob: response.body,
          filename: response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            .split(';')[0],
        }))
      );
  }

  public exportCanceled(params: {
    from: string;
    to: string;
  }): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.API_URL}/export/canceled`, {
        responseType: 'blob',
        observe: 'response',
        params,
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          blob: response.body,
          filename: response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            .split(';')[0],
        }))
      );
  }
}
